import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export const textMedia1_3RichTextCss = /*#__PURE__*/_css(["", " > :last-child{", "}"], {
  "display": "flex",
  "flex": "1 1 0%",
  "flexDirection": "column"
}, {
  "marginTop": "auto",
  "paddingTop": "2.4rem"
});
export type TextMediaBlock1_3ViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  media?: React.ReactNode;
  mediaPosition?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function TextMediaBlock1_3View({
  anchor,
  hasTopDivider = true,
  headline,
  link,
  media,
  mediaPosition = "right",
  mode = "light",
  sectionProps,
  text,
  theme = "white"
}: TextMediaBlock1_3ViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  // v3: white theme renders true white (helper maps "white" to grey-20).
  const surface = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  const textCol = <_StyledDiv key="text-col" $_css2={[{
    "position": "relative",
    "zIndex": "10",
    "gridColumn": "1 / -1",
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "justifyContent": "center",
    "rowGap": "1.6rem",
    "padding": "4rem",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 4 / span 4",
      "padding": "8rem"
    }
  }, surface]}>
      {headline && <_StyledHeadline type="h2" text={headline} />}
      {text}
      {isLinkValid(cta) && <_StyledDiv2>
          <Button as={Link} {...cta} variant="underline" icon={icon} />
        </_StyledDiv2>}
    </_StyledDiv>;
  const mediaCol = <_StyledDiv3 key="media-col" $_css3={[{
    "gridColumn": "1 / -1",
    "padding": "1.6rem",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 8 / span 8",
      "padding": "4rem"
    }
  }, themeStyle.border, surface]}>
      <_StyledDiv4>{media}</_StyledDiv4>
    </_StyledDiv3>;
  const columns = mediaPosition === "right" ? [textCol, mediaCol] : [mediaCol, textCol];
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css4={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, surface]}>
      <Wrap mode={mode as Mode}>
        <_StyledGrid $_css5={[{
        "> :not([hidden]) ~ :not([hidden])": {
          "--tw-divide-y-reverse": "0",
          "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
          "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
        },
        "@media (min-width: 1024px)": {
          "@media (min-width: 1024px)": {
            "minHeight": "650px"
          },
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-x-reverse": "0",
            "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
            "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))",
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
          }
        }
      }, themeStyle.border]}>{columns}</_StyledGrid>
      </Wrap>
    </_StyledSection>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1xbfw8y-0"
})`${p => p.$_css2}`;
var _StyledHeadline = _styled2(Headline).withConfig({
  componentId: "sc-sf-1xbfw8y-1"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1xbfw8y-2"
})({
  "marginTop": "auto"
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-1xbfw8y-3"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1xbfw8y-4"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});
var _StyledSection = _styled6("section").withConfig({
  componentId: "sc-sf-1xbfw8y-5"
})`${p => p.$_css4}`;
var _StyledGrid = /*#__PURE__*/_styled7(Grid).withConfig({
  displayName: "text-media1_3___StyledGrid",
  componentId: "sc-sgbz32-0"
})(["", ""], p => p.$_css5);